import React, { Component } from "react";
import {
  Spin,
  Table,
  DatePicker,
  Button,
  Space,
  Radio,
  Progress,
  Tag,
  Drawer,
  Empty,
  Typography,
  Popover,
  message,
} from "antd";
import {
  RiseOutlined,
  FallOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { PmsAPI } from "../../../controller/PmsAPI";
import ResponsiveECharts from "../../components/ResponsiveECharts";
import CSVDownloadButton from "../../components/CSVDownloadButton";
import { periodRange } from "./periodRange";
import DetailScopeHeader from "./DetailScopeHeader";
import { Money, pairColumn, breakdownColumns } from "./columnHelpers";

const DATE_FMT = "YYYY/MM/DD";
const PRESETS = [
  { unit: "month", label: "本月" },
  { unit: "quarter", label: "本季" },
  { unit: "year", label: "本年" },
];

// BCG 式象限配色
const QUAD_COLOR = { 明星: "green", 金牛: "blue", 問題: "orange", 瘦狗: "red", 新進: "purple" };

export class ChannelPage extends Component {
  state = {
    loading: false,
    granularity: "month", // month | quarter | year | custom
    anchor: moment(),
    from: moment().startOf("month"),
    to: moment().endOf("month"), // stay 模式取完整期間；切到 book 時由 applyCalendar 收斂到今天
    prevInfo: { from: "", to: "" }, // 後端實際採用的上期區間 (供 Popover 顯示)
    rows: [],
    base: "nights", // nights | rev | net — 佔比與環比的基準
    basis: "stay", // stay | book — 查詢維度：依入住日 / 依進單日
    // 下鑽 Drawer
    drawerOpen: false,
    drawerLoading: false,
    current: null,
    detail: null, // { basis, scope, type, from, to, trend, breakdown, breakdown_label, breakdown_key }
    detailType: "day", // day | month | year (只在長跨度下有意義)
    detailScope: "period", // period = 排行表區間 (合計對得上) | span = 長跨度趨勢
  };

  // 遞增序號:非同步回應回來時比對,丟棄過期 (快速切換區間/粒度時的 race) 回應
  _loadSeq = 0;
  _detailSeq = 0;

  componentDidMount() {
    this.load();
  }

  applyCalendar = (unit, anchor) => {
    this.setState(
      { granularity: unit, anchor, ...periodRange(unit, anchor, this.state.basis) },
      this.load
    );
  };

  setPreset = (unit) => this.applyCalendar(unit, moment());

  // 上一期 / 下一期:日曆模式跳一個完整單位;自訂區間則以區間長度滑動
  navigate = (dir) => {
    const { granularity, anchor, from, to } = this.state;
    if (granularity === "custom") {
      const len = to.diff(from, "days") + 1;
      this.setState(
        { from: from.clone().add(dir * len, "days"), to: to.clone().add(dir * len, "days") },
        this.load
      );
    } else {
      this.applyCalendar(granularity, anchor.clone().add(dir, granularity));
    }
  };

  onRangeChange = (range) => {
    if (!range || !range[0] || !range[1]) return;
    this.setState(
      { granularity: "custom", anchor: range[0].clone(), from: range[0], to: range[1] },
      this.load
    );
  };

  onBaseChange = (e) => this.setState({ base: e.target.value });

  // 切換查詢維度:兩個維度的預設區間規則不同 (見 periodRange),故一併重算本期範圍。
  // 自訂區間保留使用者選的日期不動 —— 他明確指定過，不該被我們改掉。
  onBasisChange = (e) => {
    const basis = e.target.value;
    const { granularity, anchor } = this.state;
    const next = granularity === "custom" ? {} : periodRange(granularity, anchor, basis);
    // rows 一併清空:新 columns 若先配到舊 shape 的 rows 渲染一次 (例如 stay→book 時 r.added
    // 是 undefined),share 會變 NaN,畫出一整排 <Progress percent={NaN}> 的壞畫面。
    this.setState({ basis, rows: [], ...next }, this.load);
  };

  load = () => {
    const { from, to } = this.state;
    const seq = ++this._loadSeq;
    this.setState({ loading: true });
    PmsAPI.channel
      .stat(from.format(DATE_FMT), to.format(DATE_FMT), this.state.basis)
      .then((res) => {
        if (seq !== this._loadSeq) return; // 丟棄過期回應
        const d = res.data || {};
        this.setState({ rows: d.rows || [], prevInfo: { from: d.prev_from, to: d.prev_to } });
      })
      .catch((err) => {
        if (seq !== this._loadSeq) return;
        console.error(err);
        message.error("載入通路統計失敗");
      })
      .finally(() => {
        if (seq === this._loadSeq) this.setState({ loading: false });
      });
  };

  periodLabel = () => {
    const { granularity, anchor, from, to } = this.state;
    if (granularity === "month") return anchor.format("YYYY/MM");
    if (granularity === "quarter") return anchor.format("YYYY [Q]Q");
    if (granularity === "year") return anchor.format("YYYY");
    return `${from.format(DATE_FMT)} ~ ${to.format(DATE_FMT)}`;
  };

  // ---- 下鑽 ----
  // 一律從「本期」開始:使用者是從排行表的某一列點進來的,先看到對得上那一列的數字,
  // 想看長期趨勢再自己切過去。
  openDetail = (record) => {
    this.setState({ drawerOpen: true, current: record, detailScope: "period", detailType: "day" }, this.loadDetail);
  };

  loadDetail = () => {
    const { current, detailType, detailScope, from, to } = this.state;
    if (!current) return;
    const period = detailScope === "period";
    const seq = ++this._detailSeq;
    this.setState({ drawerLoading: true, detail: null });
    PmsAPI.channel
      .detail(
        current.source,
        detailType,
        this.state.basis,
        period ? from.format(DATE_FMT) : null,
        period ? to.format(DATE_FMT) : null
      )
      .then((res) => {
        if (seq !== this._detailSeq) return; // 丟棄過期回應 (快速切換日/月/年)
        this.setState({ detail: res.data });
      })
      .catch((err) => {
        if (seq !== this._detailSeq) return;
        console.error(err);
        message.error("載入通路明細失敗");
      })
      .finally(() => {
        if (seq === this._detailSeq) this.setState({ drawerLoading: false });
      });
  };

  // 同一顆 Radio 決定範圍與粒度:本期的粒度由後端依區間長度給,不讓使用者配出
  // 「本期 + 每年」這種只畫得出一根柱、卻看起來像壞掉的組合。
  onDetailRangeChange = (v) => {
    this.setState(
      v === "period" ? { detailScope: "period" } : { detailScope: "span", detailType: v },
      this.loadDetail
    );
  };

  /** 明細標示一律讀回應裡的 basis/scope,與畫面上的資料同源;載入中才退回目前的選擇。 */
  detailBasis = () => (this.state.detail && this.state.detail.basis) || this.state.basis;

  closeDetail = () => this.setState({ drawerOpen: false, current: null, detail: null });

  // 依基準計算派生欄位 (佔比、環比、取消率、等級、象限),供表格與 CSV 共用。
  // 等級以「目前排行的區間」計算:單期資料長尾通路會較不穩,屬刻意的即時分級。
  displayRows = () => {
    const { rows, base, basis } = this.state;

    const totalNights = rows.reduce((s, r) => s + (basis === "book" ? r.added : r.nights), 0) || 1;
    const totalRev = rows.reduce((s, r) => s + r.rev, 0) || 1;
    const totalNet = rows.reduce((s, r) => s + (r.net_rev || 0), 0) || 1;

    // 佔比一律用毛量 (新增 / 營收 / 淨營收) 避免負分母；環比在進單模式改用淨進單
    const pick = (r) =>
      base === "nights"
        ? basis === "book"
          ? { cur: r.net, prev: r.prev_net, shareVal: r.added, total: totalNights }
          : { cur: r.nights, prev: r.prev_nights, shareVal: r.nights, total: totalNights }
        : base === "rev"
        ? { cur: r.rev, prev: r.prev_rev, shareVal: r.rev, total: totalRev }
        : { cur: r.net_rev || 0, prev: r.prev_net_rev || 0, shareVal: r.net_rev || 0, total: totalNet };

    const enriched = rows.map((r) => {
      const { cur, prev, shareVal, total } = pick(r);
      // 入住日:取消率分母 = 總需求 (售出 + 取消 + No-show);進單:取消佔比分母 = 新增
      const denom = basis === "book" ? r.added : r.nights + r.cancelled + r.noshow;
      // 進單模式下分母 (新增) 為 0 時回 null 而非 0——純取消的通路全部都是取消，
      // 顯示「0.0%」會讀成「沒有取消」，必須用 null 讓欄位改顯示破折號。
      // 入住日模式分母是總需求，不會是 0 卻有取消，維持原本 0 的行為。
      const cancelRate = denom > 0 ? (r.cancelled / denom) * 100 : basis === "book" ? null : 0;
      return {
        ...r,
        share: (shareVal / total) * 100,
        delta: prev > 0 ? ((cur - prev) / prev) * 100 : null,
        cancel_rate: cancelRate,
      };
    });

    // ABC 分級:依佔比由大到小累積,累積 <80% 為 A (核心)、<95% 為 B、其餘 C (長尾)
    const abc = {};
    let cum = 0;
    [...enriched]
      .sort((a, b) => b.share - a.share)
      .forEach((r) => {
        abc[r.source] = cum < 80 ? "A" : cum < 95 ? "B" : "C";
        cum += r.share;
      });

    // 象限:佔比 (以中位數為界) × 成長 (較上期,以 0% 為界);無上期則為「新進」
    const sortedShares = enriched.map((r) => r.share).sort((a, b) => a - b);
    const medShare = sortedShares.length ? sortedShares[Math.floor((sortedShares.length - 1) / 2)] : 0;

    return enriched.map((r) => {
      let quadrant;
      if (r.delta === null) {
        quadrant = "新進";
      } else {
        const hiShare = r.share >= medShare;
        const hiGrowth = r.delta > 0;
        quadrant = hiShare ? (hiGrowth ? "明星" : "金牛") : hiGrowth ? "問題" : "瘦狗";
      }
      return { ...r, abc: abc[r.source], quadrant };
    });
  };

  deltaCell = (v) => {
    if (v === null || v === undefined) return <span style={{ color: "#bfbfbf" }}>—</span>;
    if (Math.abs(v) < 0.05) return <span style={{ color: "#8c8c8c" }}>0.0%</span>; // 持平以中性色
    const up = v > 0;
    return (
      <span style={{ color: up ? "#3f8600" : "#cf1322" }}>
        {up ? <RiseOutlined /> : <FallOutlined />} {Math.abs(v).toFixed(1)}%
      </span>
    );
  };

  // 佣金相關欄位。佣金率與查詢維度無關，兩個維度共用同一組呈現
  commissionColumns = () => [
    {
      title: "佣金", dataIndex: "commission", key: "commission", align: "right", width: 120,
      sorter: (a, b) => a.commission - b.commission,
      render: (v, r) =>
        v > 0 ? (
          <span>
            {+(v * 100).toFixed(2)}%{" "}
            <Tag color={r.deduct ? "orange" : "default"} style={{ marginInlineEnd: 0 }}>
              {r.deduct ? "另扣" : "內含"}
            </Tag>
          </span>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: (
        <span>
          淨營收
          <Popover
            title="淨營收 = 營收 × (1 − 佣金)"
            content={<div style={{ maxWidth: 240 }}>扣掉 OTA 佣金後的實收;佣金設定於「設定 → 通路佣金」。未設定的來源 (直客/契約旅行社) 視為 0%。</div>}
          >
            <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
          </Popover>
        </span>
      ),
      children: [
        { title: "本期", dataIndex: "net_rev", key: "net_rev", align: "right", width: 130, sorter: (a, b) => a.net_rev - b.net_rev, render: (v) => <Money value={v} /> },
        { title: "上期", dataIndex: "prev_net_rev", key: "prev_net_rev", align: "right", width: 130, sorter: (a, b) => a.prev_net_rev - b.prev_net_rev, render: (v) => <span style={{ color: "#999" }}><Money value={v} /></span> },
      ],
    },
    {
      title: "淨ADR", dataIndex: "net_adr", key: "net_adr", align: "right", width: 110,
      sorter: (a, b) => (a.net_adr ?? -Infinity) - (b.net_adr ?? -Infinity),
      // null:純取消的列 (新增 0) 沒有淨均價可言，破折號才不會被讀成「淨均價 0 元」
      render: (v) => (v === null || v === undefined ? <span style={{ color: "#bfbfbf" }}>—</span> : <Money value={v} />),
    },
  ];

  // 「通路」與「等級」兩欄供兩個維度共用 —— 等級只依賴 share 與 delta，在進單維度同樣成立
  sourceColumn = () => ({
    title: "通路 (訂房來源)", dataIndex: "source", key: "source", width: 220,
  });

  gradeColumn = () => {
    const { base, basis } = this.state;
    // book + nights 時 share 吃的是 shareVal: r.added (見 displayRows 的 pick())，即「新增」而非「淨進單」
    const baseLabel =
      base === "nights" ? (basis === "book" ? "新增" : "房晚") : base === "rev" ? "營收" : "淨營收";
    return {
      title: (
        <span>
          等級
          <Popover
            title="通路等級 (依目前區間)"
            content={
              <div style={{ maxWidth: 300 }}>
                <div>ABC:依{baseLabel}佔比由大到小累積 —— A 為累積 80% 內的核心、B 至 95%、C 長尾。</div>
                <div style={{ marginTop: 6 }}>象限:佔比 (中位數為界) × 成長 (較上期,0% 為界) —— 明星=高佔比高成長、金牛=高佔比穩定、問題=低佔比高成長、瘦狗=低佔比衰退、新進=無上期可比。</div>
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
          </Popover>
        </span>
      ),
      key: "grade",
      width: 130,
      render: (t, r) => (
        <Space size={4}>
          <Tag color={r.abc === "A" ? "gold" : r.abc === "B" ? "blue" : "default"} style={{ marginInlineEnd: 0 }}>{r.abc}</Tag>
          <Tag color={QUAD_COLOR[r.quadrant]} style={{ marginInlineEnd: 0 }}>{r.quadrant}</Tag>
        </Space>
      ),
    };
  };

  stayColumns = () => {
    const { base, prevInfo } = this.state;
    const baseLabel = base === "nights" ? "房晚" : base === "rev" ? "營收" : "淨營收";
    return [
      this.sourceColumn(),
      this.gradeColumn(),
      {
        title: (
          <span>
            房晚
            <Popover
              title={`本期 (${this.state.from.format(DATE_FMT)} ~ ${this.state.to.format(DATE_FMT)})　vs　上期 (${prevInfo.from || ""} ~ ${prevInfo.to || ""})`}
              content={<div style={{ maxWidth: 280 }}>上期為緊鄰本期之前、等長的前一段區間；兩期天數相同，避免長度不一致造成的失真。</div>}
            >
              <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
            </Popover>
          </span>
        ),
        children: [
          { title: "本期", dataIndex: "nights", key: "nights", align: "right", width: 100, defaultSortOrder: "descend", sorter: (a, b) => a.nights - b.nights },
          { title: "上期", dataIndex: "prev_nights", key: "prev_nights", align: "right", width: 100, sorter: (a, b) => a.prev_nights - b.prev_nights, render: (v) => <span style={{ color: "#999" }}>{v}</span> },
        ],
      },
      {
        title: "營收",
        children: [
          { title: "本期", dataIndex: "rev", key: "rev", align: "right", width: 130, sorter: (a, b) => a.rev - b.rev, render: (v) => <Money value={v} /> },
          { title: "上期", dataIndex: "prev_rev", key: "prev_rev", align: "right", width: 130, sorter: (a, b) => a.prev_rev - b.prev_rev, render: (v) => <span style={{ color: "#999" }}><Money value={v} /></span> },
        ],
      },
      {
        title: "ADR",
        children: [
          { title: "本期", dataIndex: "adr", key: "adr", align: "right", width: 110, sorter: (a, b) => a.adr - b.adr, render: (v) => <Money value={v} /> },
          { title: "上期", dataIndex: "prev_adr", key: "prev_adr", align: "right", width: 110, sorter: (a, b) => a.prev_adr - b.prev_adr, render: (v) => <span style={{ color: "#999" }}><Money value={v} /></span> },
        ],
      },
      ...this.commissionColumns(),
      {
        title: `較上期 (${baseLabel})`,
        dataIndex: "delta",
        key: "delta",
        align: "right",
        width: 130,
        sorter: (a, b) => (a.delta ?? -Infinity) - (b.delta ?? -Infinity),
        render: (v) => this.deltaCell(v),
      },
      {
        title: `佔比 (${baseLabel})`,
        dataIndex: "share",
        key: "share",
        width: 160,
        sorter: (a, b) => a.share - b.share,
        render: (v) => <Progress percent={Number(v.toFixed(1))} strokeWidth={10} />,
      },
      { title: "取消", dataIndex: "cancelled", key: "cancelled", align: "right", width: 90, sorter: (a, b) => a.cancelled - b.cancelled },
      { title: "No-show", dataIndex: "noshow", key: "noshow", align: "right", width: 90, sorter: (a, b) => a.noshow - b.noshow },
      { title: "取消率", dataIndex: "cancel_rate", key: "cancel_rate", align: "right", width: 100, sorter: (a, b) => a.cancel_rate - b.cancel_rate, render: (v) => `${v.toFixed(1)}%` },
      { title: "明細", key: "action", align: "center", width: 80, render: (t, r) => <Button size="small" onClick={() => this.openDetail(r)}>明細</Button> },
    ];
  };

  bookColumns = () => {
    const { base, prevInfo, from, to } = this.state;
    const baseLabel = base === "nights" ? "淨進單" : base === "rev" ? "營收" : "淨營收";
    const periodPopover = (
      <Popover
        title={`本期 (${from.format(DATE_FMT)} ~ ${to.format(DATE_FMT)})　vs　上期 (${prevInfo.from || ""} ~ ${prevInfo.to || ""})`}
        content={<div style={{ maxWidth: 300 }}>上期為緊鄰本期之前、同樣天數的區間。新增依訂單成立日、取消依取消日各自加總 —— 同一筆單在區間內既成立又取消時兩邊都計，淨值為 0。</div>}
      >
        <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
      </Popover>
    );
    return [
      this.sourceColumn(),
      this.gradeColumn(),
      pairColumn(<span>新增 (房晚){periodPopover}</span>, "added", "prev_added"),
      pairColumn("取消 (房晚)", "cancelled", "prev_cancelled"),
      pairColumn("淨進單", "net", "prev_net"),
      pairColumn("營收", "rev", "prev_rev", { money: true, width: 130 }),
      pairColumn("ADR", "adr", "prev_adr", { money: true, width: 110 }),
      ...this.commissionColumns(),
      {
        title: `較上期 (${baseLabel})`, dataIndex: "delta", key: "delta", align: "right", width: 130,
        sorter: (a, b) => (a.delta ?? -Infinity) - (b.delta ?? -Infinity),
        render: (v) => this.deltaCell(v),
      },
      {
        title: (
          <span>
            佔比 ({base === "nights" ? "新增" : baseLabel})
            <Popover
              title="佔比以毛量為分母"
              content={
                <div style={{ maxWidth: 300 }}>
                  {base === "nights"
                    ? "房晚基準用新增而非淨進單:淨值可能為負，負佔比畫不出來，且單一負值會把其他通路的佔比推過 100%。「較上期」則用淨進單，看的是扣掉退單後的真實動能。"
                    : `${baseLabel}基準的佔比與較上期皆以${baseLabel}計算，與新增／淨進單無關。`}
                </div>
              }
            >
              <InfoCircleOutlined style={{ color: "#bfbfbf", marginLeft: 4, cursor: "help" }} />
            </Popover>
          </span>
        ),
        dataIndex: "share", key: "share", width: 160,
        sorter: (a, b) => a.share - b.share,
        render: (v) => <Progress percent={Number(v.toFixed(1))} strokeWidth={10} />,
      },
      {
        title: "取消佔比", dataIndex: "cancel_rate", key: "cancel_rate", align: "right", width: 100,
        sorter: (a, b) => (a.cancel_rate ?? -Infinity) - (b.cancel_rate ?? -Infinity),
        render: (v) => (v === null || v === undefined ? <span style={{ color: "#bfbfbf" }}>—</span> : `${v.toFixed(1)}%`),
      },
      { title: "明細", key: "action", align: "center", width: 80,
        render: (t, r) => <Button size="small" onClick={() => this.openDetail(r)}>明細</Button> },
    ];
  };

  columns = () => (this.state.basis === "book" ? this.bookColumns() : this.stayColumns());

  // 只覆寫「需轉成文字格式」的欄;純數字欄 CSVDownloadButton 會原樣輸出,無需 override
  csvOverride = () => ({
    grade: (v, r) => `${r.abc} / ${r.quadrant}`,
    commission: (v, r) => (Number(v) > 0 ? `${+(Number(v) * 100).toFixed(2)}% ${r.deduct ? "另扣" : "內含"}` : "—"),
    share: (v) => `${Number(v).toFixed(1)}%`,
    delta: (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`),
    cancel_rate: (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`),
    adr: (v) => (v === null || v === undefined ? "—" : String(v)),
    prev_adr: (v) => (v === null || v === undefined ? "—" : String(v)),
    net_adr: (v) => (v === null || v === undefined ? "—" : String(v)),
  });

  trendOption = () => {
    const trend = (this.state.detail && this.state.detail.trend) || [];
    const book = this.detailBasis() === "book";
    const nightsLabel = book ? "新增房晚" : "房晚";
    return {
      tooltip: { trigger: "axis" },
      legend: { data: [nightsLabel, "營收"] },
      grid: { left: 56, right: 56, top: 36, bottom: 56 },
      // X 軸掛上日期身分:同一張圖在兩個基準下畫的是不同的日子,不標會被當成同一回事。
      // 名稱置中放在軸下方 (而非軸末端) —— 末端會壓到最後一個日期刻度,右邊界還會把字切掉
      xAxis: {
        type: "category",
        data: trend.map((t) => t.dat),
        name: book ? "訂房日" : "入住日",
        nameLocation: "middle",
        nameGap: 32,
        nameTextStyle: { color: "#8c8c8c" },
      },
      yAxis: [
        { type: "value", name: nightsLabel },
        { type: "value", name: "營收", position: "right", splitLine: { show: false } },
      ],
      series: [
        { name: nightsLabel, type: "line", smooth: true, yAxisIndex: 0, data: trend.map((t) => t.nights) },
        { name: "營收", type: "line", smooth: true, yAxisIndex: 1, data: trend.map((t) => t.rev) },
      ],
    };
  };

  render() {
    const { loading, granularity, from, to, base, basis, drawerOpen, drawerLoading, current, detail, detailType, detailScope } = this.state;
    const rows = this.displayRows();
    const columns = this.columns();
    // 明細的基準與範圍都以回應為準 (載入中才用目前的選擇),標示才不會早於資料變動
    const dBasis = this.detailBasis();
    const dScope = (detail && detail.scope) || detailScope;

    return (
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space align="baseline" wrap>
            <Typography.Title level={3} style={{ margin: 0 }}>通路統計</Typography.Title>
            <Popover
              title="口徑說明"
              content={
                basis === "book" ? (
                  <div style={{ maxWidth: 320 }}>
                    以訂單成立日 (ORDER_DT.INS_DAT) 統計各通路的進單狀況：新增依成立日、取消依取消日各自加總，淨進單 = 新增 − 取消，佣金照舊套用。
                    <div style={{ marginTop: 6 }}>
                      統計母體是「這段期間成立的訂單」(不論何時入住)，與「依入住日」的「這段期間住用的房晚」(不論何時訂的) 不是同一批訂單，數字不宜直接相減。
                    </div>
                  </div>
                ) : (
                  <div style={{ maxWidth: 320 }}>
                    以入住日 (RVUSE_DT.BATCH_DAT) 統計各通路的淨售出房晚與營收 (房租 + 餐服)。
                    區間含未來日期，屬「on-the-books (在帳)」預估，非已實現營收 —— 本期未來的部分還在持續進單，「較上期」會偏低，佔比不受影響。
                  </div>
                )
              }
            >
              <InfoCircleOutlined style={{ color: "#bfbfbf", cursor: "help" }} />
            </Popover>
          </Space>

          <Space wrap>
            <Radio.Group value={basis} onChange={this.onBasisChange} buttonStyle="solid">
              <Radio.Button value="stay">依入住日</Radio.Button>
              <Radio.Button value="book">依進單日</Radio.Button>
            </Radio.Group>
            {PRESETS.map((p) => (
              <Button
                key={p.unit}
                type={granularity === p.unit ? "primary" : "default"}
                onClick={() => this.setPreset(p.unit)}
              >
                {p.label}
              </Button>
            ))}
            <Button.Group>
              <Button icon={<LeftOutlined />} onClick={() => this.navigate(-1)}>上一期</Button>
              <Button style={{ cursor: "default", minWidth: 120 }}>{this.periodLabel()}</Button>
              <Button onClick={() => this.navigate(1)}>下一期 <RightOutlined /></Button>
            </Button.Group>
            <DatePicker.RangePicker value={[from, to]} format={DATE_FMT} allowClear={false} onChange={this.onRangeChange} />
            <Radio.Group value={base} onChange={this.onBaseChange} buttonStyle="solid">
              <Radio.Button value="nights">佔比:房晚</Radio.Button>
              <Radio.Button value="rev">佔比:營收</Radio.Button>
              <Radio.Button value="net">佔比:淨營收</Radio.Button>
            </Radio.Group>
            <CSVDownloadButton columns={columns} data={rows} filename="通路統計" overrideRender={this.csvOverride()}>
              <DownloadOutlined /> 匯出 CSV
            </CSVDownloadButton>
          </Space>

          <Table
            rowKey="source"
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <Empty description="此區間內無通路資料" /> }}
          />
        </Space>

        <Drawer
          title={
            <Space size={8}>
              <span>{current ? current.source : ""}</span>
              <Tag color={dBasis === "book" ? "purple" : "blue"} style={{ marginRight: 0 }}>
                {dBasis === "book" ? "依進單日" : "依入住日"}
              </Tag>
            </Space>
          }
          width={760}
          visible={drawerOpen}
          onClose={this.closeDetail}
          destroyOnClose
        >
          <Spin spinning={drawerLoading}>
            <DetailScopeHeader
              scope={dScope}
              type={detailType}
              basis={dBasis}
              from={detail && detail.from}
              to={detail && detail.to}
              onChange={this.onDetailRangeChange}
            />
            <div>
              {detail && detail.trend && detail.trend.length ? (
                <ResponsiveECharts option={this.trendOption()} style={{ height: 300 }} />
              ) : (
                !drawerLoading && <Empty description="此跨度內無趨勢資料" />
              )}
            </div>
            <Typography.Title level={5} style={{ marginTop: 16 }}>
              {(detail && detail.breakdown_label) || "房型組成"}
            </Typography.Title>
            <Table
              rowKey={(detail && detail.breakdown_key) || "room_cod"}
              columns={breakdownColumns(detail)}
              dataSource={(detail && detail.breakdown) || []}
              size="small"
              pagination={false}
              locale={{ emptyText: <Empty description="無組成資料" /> }}
            />
          </Spin>
        </Drawer>
      </Spin>
    );
  }
}

export default ChannelPage;
